import {
  FC,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  KeyboardEvent,
  ChangeEvent,
  RefObject,
  useId,
} from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { ArrowUpIcon } from '@/components/base/Icons/RocheIconPack';
import { WarningCircle } from '@/components/base/Icons/ConsentsIconPack';
import { TooltipGroup, TooltipGroupAnchor } from '@/components/shared/tooltip/TooltipGroup';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import StopIcon from '@/storybook/icons/StopIcon';
import useAutoResizeTextarea from './useAutoResizeTextarea';
import { TestIds } from './testIds';
import styles from './ChatInput.module.css';

export interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  isStopping?: boolean;
  disabled?: boolean;
  submitDisabled?: boolean;
  error?: string;
  drawerRef?: RefObject<HTMLElement | null>;
  injectedValue?: string;
  injectedValueKey?: number;
}

const ChatInput: FC<ChatInputProps> = ({
  onSend,
  onStop,
  isStreaming = false,
  isStopping = false,
  disabled = false,
  submitDisabled = false,
  error,
  drawerRef,
  injectedValue,
  injectedValueKey,
}) => {
  const { t } = useTranslation();
  const tooltipGroupId = useId();
  const [value, setValue] = useState('');
  const { textareaRef, adjustHeight, resetHeight } = useAutoResizeTextarea(drawerRef);
  const errorMessageId = useId();
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const stopButtonRef = useRef<HTMLButtonElement>(null);
  const wasStopButtonShownRef = useRef(false);
  const trailingButtonHadFocusRef = useRef(false);

  const handleTrailingButtonFocus = () => {
    trailingButtonHadFocusRef.current = true;
  };

  const handleTrailingButtonBlur = () => {
    trailingButtonHadFocusRef.current = false;
  };

  useEffect(() => {
    if (injectedValue === undefined || injectedValue.length === 0) return;

    setValue(injectedValue);
    adjustHeight();
    textareaRef.current?.focus();
  }, [injectedValue, injectedValueKey, adjustHeight, textareaRef]);

  const trimmedValue = value.trim();
  const isSubmitBlocked = submitDisabled || isStreaming || isStopping;
  const isSendDisabled = isSubmitBlocked || disabled || trimmedValue.length === 0;

  const handleSend = () => {
    if (isSendDisabled) return;

    onSend(trimmedValue);
    setValue('');
    resetHeight();
    textareaRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !isSubmitBlocked) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
      adjustHeight();
    },
    [adjustHeight],
  );

  const handleStop = () => {
    if (isStopping) return;

    onStop?.();
  };

  const showStopButton = isStreaming || isStopping;

  useLayoutEffect(() => {
    const wasShown = wasStopButtonShownRef.current;
    wasStopButtonShownRef.current = showStopButton;

    if (wasShown === showStopButton) return;
    if (!trailingButtonHadFocusRef.current) return;

    const incomingButton = showStopButton ? stopButtonRef.current : sendButtonRef.current;
    if (incomingButton && !incomingButton.disabled) {
      incomingButton.focus();
    } else {
      textareaRef.current?.focus();
    }
  }, [showStopButton, textareaRef]);

  const trailingButton = showStopButton ? (
    <TooltipGroup id={`chat-input-stop-${tooltipGroupId}`} shouldShowForTruncatedOnly={false} delayShow={200}>
      <TooltipGroupAnchor value={t('components.aiChatDrawer.stop.tooltip')} place="top" forceDisplayTooltip>
        <span className={styles.actionTooltipAnchor}>
          <IconButton
            ref={stopButtonRef}
            appearance="fab"
            size="XS"
            icon={<StopIcon className={styles.stopIcon} />}
            onClick={handleStop}
            onFocus={handleTrailingButtonFocus}
            onBlur={handleTrailingButtonBlur}
            loading={isStopping}
            disabled={isStopping}
            aria-label={t('components.aiChatDrawer.stop.ariaLabel')}
            className={cx(styles.sendButton, styles.stopButton)}
            data-testid={TestIds.stopButton}
          />
        </span>
      </TooltipGroupAnchor>
    </TooltipGroup>
  ) : (
    <IconButton
      ref={sendButtonRef}
      appearance="fab"
      size="XS"
      icon={<ArrowUpIcon className={styles.sendIcon} />}
      onClick={handleSend}
      onFocus={handleTrailingButtonFocus}
      onBlur={handleTrailingButtonBlur}
      disabled={isSendDisabled}
      aria-label={t('components.aiChatDrawer.input.sendAriaLabel')}
      className={cx(styles.sendButton, !isSendDisabled && styles.sendButtonEnabled)}
      data-testid={TestIds.sendButton}
    />
  );

  return (
    <div className={styles.container} data-testid={TestIds.container}>
      <div className={cx(styles.inputWrapper, error && styles.hasError, disabled && styles.disabled)}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('components.aiChatDrawer.input.placeholder')}
          aria-label={t('components.aiChatDrawer.input.ariaLabel')}
          aria-describedby={error ? errorMessageId : undefined}
          disabled={disabled}
          rows={1}
          data-testid={TestIds.textarea}
        />
        {trailingButton}
      </div>
      {error && (
        <div className={styles.errorRow} data-testid={TestIds.errorMessage}>
          <WarningCircle className={styles.errorIcon} fill="var(--red-400)" />
          <Text as="small" id={errorMessageId} className={styles.errorText}>
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export default ChatInput;
